import type { SourcePostFilters, TypBudynku } from './types.ts';
import { KLUCZ_GARAZU, KLUCZ_SWIATLOWODU } from './types.ts';

/**
 * FILTRY MIĘKKIE — strona ZAPYTANIA (SRCH-01 etap 1b): kiedy SERP czyta przez `flat.offers_ranked`,
 * jakie predykaty „jawne nie wypada, NULL zostaje” dokłada do łańcucha i jakie preferencje
 * dostaje funkcja rankingu. Przeniesione BEZ ZMIAN z `src/lib/filtryMiekkie.ts` (które eksportuje
 * je dalej), bo ścieżka odczytu musi być importowalna z funkcji brzegowej (plan §1.68).
 * Strona KARTY (`stanWarunku`, adnotacje, i18n) została w `src/lib/filtryMiekkie.ts`.
 */

export function maMiekkieFiltry(filters: SourcePostFilters): boolean {
  return !!(
    filters.zeZwierzeciem || filters.typBudynku || filters.wiekBudynku || filters.umeblowanie || filters.pietro || filters.kuchnia || filters.lazienki ||
    filters.balkon || filters.winda || filters.kaucjaMax != null || !!filters.udogodnienia?.length || !!filters.internet ||
    !!filters.garaz
  );
}

/**
 * „Wprost nie spełnia → wypada", jako warunki PostgREST `or(...)`: NULL zostaje (nie wiadomo),
 * jawnie niepasująca wartość odpada. Każdy element tablicy to osobne `.or()` — PostgREST łączy
 * je spójnikiem AND, tak jak robi to już warunek miasta i `scraped_at` w tym samym łańcuchu.
 * Zwierzęta mają własny predykat w serpQuery.ts (starszy, z tym samym znaczeniem).
 * Balkon niczego nie wyklucza — patrz `stanWarunku`.
 */
/**
 * `p_prefs` dla HISTOGRAMU (`offers_price_histogram` → `offers_matching`): preferencje miękkie
 * (te same co dla `offers_ranked`) plus filtry twarde, które `offers_matching` zna tylko jako
 * klucze `p_prefs` — „Właściciel na FlatHero” (`owner_on_platform`, A5). Bez żadnego — `null`,
 * funkcja działa po staremu. Słupki liczą wtedy ten sam zbiór co lista.
 */
export function prefsHistogramu(filters: SourcePostFilters): Record<string, unknown> | null {
  const p: Record<string, unknown> = maMiekkieFiltry(filters) ? preferencjeRankingu(filters) : {};
  if (filters.wlascicielNaFlatHero) p.owner_on_platform = true;
  // „Ulubione” (A6): ta sama lista co predykat listy; pusta = zero ofert także w słupkach.
  if (filters.ulubione) p.offer_ids = filters.ulubioneIds ?? [];
  return Object.keys(p).length ? p : null;
}

/**
 * Wartości `building_type`, które spełniają wybrany typ. „Dom” obejmuje `townhouse` (szeregowiec
 * — ta wartość jest w danych, a słownik filtra jej nie ma); bez tego szeregowiec jako „jawnie
 * inny typ” wypadałby z listy przy filtrze „Dom”.
 */
export function wartosciTypuBudynku(typ: TypBudynku): string[] {
  return typ === 'house' ? ['house', 'townhouse'] : [typ];
}

export function predykatyWykluczenia(filters: SourcePostFilters): string[] {
  const wynik: string[] = [];
  if (filters.typBudynku) wynik.push(`building_type.is.null,building_type.in.(${wartosciTypuBudynku(filters.typBudynku).join(',')})`);
  if (filters.wiekBudynku) wynik.push(`building_age.is.null,building_age.eq.${filters.wiekBudynku}`);
  if (filters.umeblowanie === 'umeblowane') wynik.push('furnishing.is.null,furnishing.in.(furnished,partially)');
  if (filters.umeblowanie === 'nieumeblowane') wynik.push('furnishing.is.null,furnishing.eq.unfurnished');
  // Parter = piętro <= 0 (suterena też), tak jak liczą offers_ranked i histogram w bazie.
  if (filters.pietro === 'parter') wynik.push('floor.is.null,floor.lte.0');
  if (filters.kuchnia === 'oddzielna') wynik.push('kitchen_type.is.null,kitchen_type.eq.separate');
  if (filters.kuchnia === 'aneks') wynik.push('kitchen_type.is.null,kitchen_type.eq.combined');
  if (filters.lazienki === '1') wynik.push('bathrooms.is.null,bathrooms.eq.1');
  if (filters.lazienki === '2+') wynik.push('bathrooms.is.null,bathrooms.gte.2');
  // Winda i kaucja — DOKŁADNIE semantyka `offers_matching` (histogram): `has_elevator is not
  // false`, `deposit_pln is null or <= X`. Kaucję zna 2 044 z 4 291 ofert; odsiewanie nieznanych
  // zabrałoby połowę wyników.
  if (filters.winda) wynik.push('has_elevator.is.null,has_elevator.eq.true');
  if (filters.kaucjaMax != null) wynik.push(`deposit_pln.is.null,deposit_pln.lte.${Math.round(filters.kaucjaMax)}`);
  return wynik;
}

/**
 * Preferencje dla `flat.offers_ranked(p_prefs)` — szeregowanie „spełnia wyżej, nie wiadomo
 * niżej". Kontrakt funkcji: {pets, furnishing, kitchen_type, bathrooms_min, floor_max, balcony}.
 * Podpinane osobno (dziś funkcja zwraca 42501 dla anona — zgłoszone agentowi danych).
 */
export function preferencjeRankingu(filters: SourcePostFilters): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (filters.zeZwierzeciem) p.pets = true;
  // `building_type text[]` — kontrakt `offers_ranked` od 28.09 (waga 128); `offers_matching`
  // odcina po nim jawnie inny typ od migracji A3.
  if (filters.typBudynku) p.building_type = wartosciTypuBudynku(filters.typBudynku);
  // Wiek: `offers_ranked` ocenia go razem z typem (jeden wymiar „budynek”, A4).
  if (filters.wiekBudynku) p.building_age = filters.wiekBudynku;
  if (filters.umeblowanie) p.furnishing = filters.umeblowanie === 'umeblowane' ? 'furnished' : 'unfurnished';
  if (filters.kuchnia) p.kitchen_type = filters.kuchnia === 'oddzielna' ? 'separate' : 'combined';
  if (filters.lazienki === '2+') p.bathrooms_min = 2;
  // „1" = dokładnie jedna (jak predykat listy `bathrooms.eq.1`); baza zna `bathrooms_exact`
  // w offers_ranked i offers_matching. Bez tego słupki histogramu liczyłyby więcej niż lista.
  if (filters.lazienki === '1') p.bathrooms_exact = 1;
  if (filters.pietro === 'parter') p.floor_max = 0;
  if (filters.balkon) p.balcony = true;
  // Rankingu nie zmieniają (offers_ranked ich nie ocenia), ale `offers_matching` — czyli
  // histogram — wycina po nich tak samo jak lista.
  if (filters.winda) p.elevator = true;
  if (filters.kaucjaMax != null) p.deposit_max = Math.round(filters.kaucjaMax);
  // Udogodnienia: tylko porządek (offers_ranked), offers_matching nic po nich nie wycina.
  // Światłowód idzie TĄ SAMĄ drogą (kontrakt `amenities text[]` przyjmuje każdy klucz
  // amenities_norm), więc też tylko porządkuje i nie odcina niczego w liczniku ani histogramie.
  const amenities: string[] = [...(filters.udogodnienia ?? [])];
  if (filters.internet === 'swiatlowod') amenities.push(KLUCZ_SWIATLOWODU);
  // Garaż podziemny (Piotr 6.10): ten sam kontrakt, token `garage` (parking podziemny z treści).
  if (filters.garaz) amenities.push(KLUCZ_GARAZU);
  if (amenities.length) p.amenities = amenities;
  return p;
}
